// Progress update grid (design flag 2): rows = deliverables, column groups = chain steps with Planned / Forecast / Earned.
// Paste from Excel works across cells; each batch is validated on the server (rules 6, 16, 18, 21) and saved atomically.
import { useMutation } from "convex/react";
import { useMemo, useState } from "react";
import { api } from "../../../convex/_generated/api";
import type { Doc, Id } from "../../../convex/_generated/dataModel";
import { useProject } from "@/hooks/project";
import { Badge, Button, Checkbox, Input, Modal, PageHeader, Select, cn, fmtDate, fmtNum, fmtPct, useRun, useToast } from "@/components/ui";
import { DataGrid, type Col, type CellEdit } from "@/components/DataGrid";
import { deliverableStatus } from "@/engine/compute";
import { formatCum } from "@/engine/chains";
import type { Issue } from "@/engine/validate";

type D = Doc<"deliverables">;
type Field = "planned" | "forecast" | "earned" | "pbs" | "obs" | "wbs" | "planningRef";

export function ProgressPage() {
  const P = useProject();
  const { project, b, periods, currentIdx, canEdit, issues, prep, chains, nodes, activeBreakdowns } = P;
  const edit = useMutation(api.deliverables.editSteps);
  const run = useRun();
  const toast = useToast();
  const cur = periods[currentIdx];
  const lastClosed = periods.filter((p) => p.closed).at(-1);
  const [filter, setFilter] = useState<"all" | "due" | "issues" | "open">("all");
  const [dbs, setDbs] = useState("");
  const [q, setQ] = useState("");
  const [showCodes, setShowCodes] = useState(false);
  const [detail, setDetail] = useState<D | null>(null);
  const [pending, setPending] = useState<{ payload: Parameters<typeof edit>[0]["edits"]; revs: string[] } | null>(null);
  const [reason, setReason] = useState("");
  const status = useMemo(() => new Map(deliverableStatus(prep, Math.max(0, currentIdx)).map((s) => [s.id, s])), [prep, currentIdx]);
  const noPF = project.options.noPlanForecast;

  const rows = useMemo(() => {
    return b.deliverables
      .filter((d) => !dbs || d.codes.dbs === dbs)
      .filter((d) => !q || `${d.code} ${d.title ?? ""}`.toLowerCase().includes(q.toLowerCase()))
      .filter((d) => {
        if (filter === "issues") return issues.has(d._id);
        if (filter === "open") return d.steps.some((s) => !s.earned);
        if (filter === "due" && cur) {
          // Due or forecast this period (or overdue): the next unearned milestone's forecast/planned date is on or before this cutoff.
          const next = d.steps.find((s) => !s.earned);
          const dt = next?.forecast ?? next?.planned;
          return !!next && !!dt && dt <= cur.cutoff;
        }
        return true;
      })
      .sort((a, c) => a.code.localeCompare(c.code, undefined, { numeric: true }));
  }, [b.deliverables, dbs, q, filter, issues, cur]);

  const maxSteps = Math.max(1, ...rows.map((d) => d.steps.length));
  const issueAt = (d: D, step: number, field: string): Issue | undefined => issues.get(d._id)?.find((i) => i.step === step + 1 && i.field === field);

  const cols: Col<D>[] = useMemo(() => {
    const c: Col<D>[] = [
      { id: "code", title: "DLV reference", width: 240, get: (r) => r.code, tone: (r) => (issues.get(r._id)?.some((i) => i.severity === "error") ? "error" : undefined) },
      { id: "chain", title: "Chain", width: 70, get: (r) => r.chainCode },
      { id: "w", title: "Weight", width: 70, kind: "number", digits: 1, get: (r) => r.weight },
      { id: "e", title: "Earned %", width: 72, kind: "pct", get: (r) => status.get(r._id)?.pctEarned ?? null },
    ];
    const stepCodes = activeBreakdowns.filter((t) => ["pbs", "obs", "wbs"].includes(t)) as ("pbs" | "obs" | "wbs")[];
    for (let i = 0; i < maxSteps; i++) {
      const group = `Step ${i + 1}`;
      const has = (r: D) => i < r.steps.length;
      const chainStep = (r: D) => chains.get(r.chainCode)?.steps[i];
      c.push({ id: `ms${i}`, title: "MS", group, width: 92, get: (r) => (has(r) ? `${r.steps[i].milestone} ${chainStep(r) ? formatCum(chainStep(r)!) : ""}` : ""), tone: (r) => (has(r) ? (r.steps[i].earned ? "muted" : undefined) : "muted") });
      const dateCol = (field: "planned" | "forecast" | "earned", title: string): Col<D> => ({
        id: `${field}${i}`,
        title,
        group,
        width: 84,
        kind: "date",
        get: (r) => (has(r) ? r.steps[i][field] ?? null : null),
        set: canEdit ? () => {} : undefined,
        readonly: (r) => {
          if (!has(r)) return true;
          if (field === "planned") return project.baselineLocked;
          if (field === "earned" && lastClosed) return !!r.steps[i].earned && r.steps[i].earned! <= lastClosed.cutoff;
          return false;
        },
        tone: (r) => {
          if (!has(r)) return "muted";
          const is = issueAt(r, i, field);
          if (is) return is.severity === "error" ? "error" : "warn";
          if (field === "forecast" && status.get(r._id)?.derivedForecast.includes(i)) return "derived";
          return undefined;
        },
      });
      if (!noPF) c.push(dateCol("planned", "Planned"));
      if (!noPF) {
        const fc = dateCol("forecast", "Forecast");
        // Auto-forecast (rule 22) shows the derived date, shaded blue.
        // Auto-forecast (rule 22) and "forecast = plan until set" both show as derived (blue) values.
        fc.get = (r) => {
          if (!has(r)) return null;
          const s = r.steps[i];
          if (status.get(r._id)?.derivedForecast.includes(i)) return status.get(r._id)!.forecastSteps[i];
          return s.forecast ?? (s.earned ? null : s.planned ?? null);
        };
        const baseTone = fc.tone!;
        fc.tone = (r) => baseTone(r) ?? (has(r) && !r.steps[i].forecast && !r.steps[i].earned && r.steps[i].planned ? "derived" : undefined);
        c.push(fc);
      }
      c.push(dateCol("earned", "Earned"));
      if (showCodes) {
        for (const t of stepCodes)
          c.push({
            id: `${t}${i}`,
            title: t.toUpperCase(),
            group,
            width: 64,
            options: nodes[t].map((n) => n.code),
            get: (r) => (has(r) ? r.steps[i].codes?.[t] : null),
            set: canEdit ? () => {} : undefined,
            readonly: (r) => !has(r),
            tone: (r) => (has(r) ? (issueAt(r, i, t) ? "error" : undefined) : "muted"),
          });
        c.push({ id: `ref${i}`, title: "P6 ref", group, width: 80, get: (r) => (has(r) ? r.steps[i].planningRef : null), set: canEdit ? () => {} : undefined, readonly: (r) => !has(r) });
      }
    }
    return c;
  }, [maxSteps, chains, canEdit, project.baselineLocked, lastClosed, issues, status, noPF, showCodes, activeBreakdowns, nodes]);

  const onEdit = async (edits: CellEdit<D>[]) => {
    const payload = edits.map((e) => {
      const m = e.col.id.match(/^([a-zA-Z]+?)(\d+)$/)!;
      const f = (m[1] === "ref" ? "planningRef" : m[1]) as Field;
      return { id: e.row._id as Id<"deliverables">, step: Number(m[2]), field: f, value: e.value === null ? null : String(e.value) };
    });
    return submit(payload);
  };
  const submit = async (payload: Parameters<typeof edit>[0]["edits"]) => {
    // X07: changing or clearing an earned date un-earns progress, so ask why first (the server enforces it too).
    const revs = payload
      .filter((p) => p.field === "earned")
      .flatMap((p) => {
        const d = b.deliverables.find((x) => x._id === p.id);
        const old = d?.steps[p.step]?.earned ?? null;
        return old && old !== p.value ? [`${d!.code} ${d!.steps[p.step].milestone}: ${fmtDate(old)} → ${p.value ? fmtDate(p.value) : "cleared"}`] : [];
      });
    if (revs.length) {
      setReason("");
      setPending({ payload, revs });
      return undefined;
    }
    return await save(payload);
  };
  const save = async (payload: Parameters<typeof edit>[0]["edits"], why?: string) => {
    const n = await run(() => edit({ projectId: project._id, edits: payload, reason: why }));
    if (n !== undefined && payload.length > 1) toast({ text: `Saved ${payload.length} cells on ${n} deliverables`, tone: "success" });
    return n;
  };

  const dbsOptions = nodes.dbs;
  return (
    <div className="space-y-3">
      <PageHeader
        title="Progress update"
        sub={
          <>
            {cur ? (
              <>
                Current period <b>P{cur.number}</b> ({fmtDate(cur.start)} to {fmtDate(cur.cutoff)}).{" "}
              </>
            ) : (
              "Set up the calendar first. "
            )}
            An earned date earns the step's cumulative %. A forecast date earns nothing. Steps can't be skipped and earned dates can't go backwards.
            <br />
            To enter dates: click a date cell and type (e.g. 15-Jul-26), paste a block from Excel, or <b>click a DLV reference</b> to edit that deliverable in a form with date pickers. Blue forecast dates are filled in for you (from the plan, or auto-forecast).
          </>
        }
      />
      <div className="flex flex-wrap items-center gap-3">
        <Input placeholder="Filter" value={q} onChange={(e) => setQ(e.target.value)} className="w-56" aria-label="Filter" />
        <Select value={filter} onChange={(e) => setFilter(e.target.value as "all")} className="w-60" aria-label="Show">
          <option value="all">All deliverables</option>
          <option value="due">Due or forecast by this cutoff</option>
          <option value="open">Not complete</option>
          <option value="issues">With issues</option>
        </Select>
        <Select value={dbs} onChange={(e) => setDbs(e.target.value)} className="w-48" aria-label="Discipline">
          <option value="">All disciplines</option>
          {dbsOptions.map((n) => (
            <option key={n.code} value={n.code}>
              {n.code} · {n.title}
            </option>
          ))}
        </Select>
        <Checkbox label="Show step codes and P6 refs" checked={showCodes} onChange={setShowCodes} />
        <span className="text-sm text-slate-500">{rows.length} shown</span>
        <span className="ml-auto flex gap-2 text-xs">
          <Badge tone="red">error</Badge>
          <Badge tone="amber">warning</Badge>
          <Badge tone="blue">auto-forecast</Badge>
          {project.baselineLocked && <Badge tone="amber">planned dates locked</Badge>}
        </span>
      </div>
      <DataGrid rows={rows} cols={cols} freeze={2} height="calc(100vh - 250px)" onEdit={onEdit} onError={(m) => toast({ text: m, tone: "error" })} onCellClick={(r, c) => c.id === "code" && setDetail(r)} headerHeight={30} />
      {detail && <Detail d={b.deliverables.find((x) => x._id === detail._id) ?? detail} onClose={() => setDetail(null)} submit={submit} />}
      <Modal
        open={!!pending}
        onClose={() => setPending(null)}
        title="Why is this earned date changing?"
        footer={
          <>
            <Button onClick={() => setPending(null)}>Cancel</Button>
            <Button
              variant="primary"
              disabled={!reason.trim()}
              onClick={async () => {
                const n = await save(pending!.payload, reason.trim());
                if (n !== undefined) setPending(null);
              }}
            >
              Save with reason
            </Button>
          </>
        }
      >
        <p className="mb-2 text-sm text-slate-600">This removes or moves progress that was already earned. The reason goes in the audit log.</p>
        <ul className="mb-3 list-inside list-disc text-sm">
          {pending?.revs.map((r) => (
            <li key={r}>{r}</li>
          ))}
        </ul>
        <Input aria-label="Reason" placeholder="e.g. entered against the wrong document" value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
      </Modal>
    </div>
  );
}

/** Per-deliverable form: every step with its share and value, and date pickers for Planned / Forecast / Earned. */
function Detail({ d, onClose, submit }: { d: D; onClose: () => void; submit: (p: { id: Id<"deliverables">; step: number; field: "planned" | "forecast" | "earned"; value: string | null }[]) => Promise<unknown> }) {
  const { chains, issues, prep, currentIdx, project, canEdit, periods } = useProject();
  const chain = chains.get(d.chainCode);
  const st = deliverableStatus(prep, Math.max(0, currentIdx)).find((s) => s.id === d._id);
  const lastClosed = periods.filter((p) => p.closed).at(-1);
  const [draft, setDraft] = useState(() => d.steps.map((s) => ({ planned: s.planned ?? "", forecast: s.forecast ?? "", earned: s.earned ?? "" })));
  const [busy, setBusy] = useState(false);
  const changed = draft.flatMap((row, i) =>
    (["planned", "forecast", "earned"] as const).filter((f) => (row[f] || null) !== (d.steps[i]?.[f] ?? null)).map((f) => ({ id: d._id as Id<"deliverables">, step: i, field: f, value: row[f] || null })),
  );
  const lockedField = (i: number, f: "planned" | "forecast" | "earned") =>
    !canEdit || (f === "planned" && project.baselineLocked) || (f === "earned" && !!lastClosed && !!d.steps[i].earned && d.steps[i].earned! <= lastClosed.cutoff) || (project.options.noPlanForecast && f !== "earned");
  return (
    <Modal
      open
      onClose={onClose}
      title={`${d.code} ${d.title ?? ""}`}
      wide
      footer={
        <>
          <Button onClick={onClose}>Close</Button>
          {canEdit && (
            <Button
              variant="primary"
              busy={busy}
              disabled={!changed.length}
              onClick={async () => {
                setBusy(true);
                const r = await submit(changed);
                setBusy(false);
                if (r !== undefined) onClose();
              }}
            >
              Save {changed.length ? `${changed.length} change${changed.length > 1 ? "s" : ""}` : ""}
            </Button>
          )}
        </>
      }
    >
      <div className="mb-3 flex flex-wrap gap-2 text-sm">
        <Badge>Chain {d.chainCode}</Badge>
        <Badge>
          Weight {fmtNum(st?.weight ?? d.weight, 1)} {d.unit === "$" ? "$" : "h"}
        </Badge>
        <Badge tone="green">
          Earned {fmtPct(st?.pctEarned)} ({fmtNum(st?.ev, 1)})
        </Badge>
        <Badge tone="blue">Planned {fmtPct(st?.pctPlanned)}</Badge>
      </div>
      {d.loe && (
        <p className="mb-3 rounded border border-slate-200 bg-slate-50 p-2 text-sm text-slate-600">
          Level of effort from {fmtDate(d.loe.start)} to {fmtDate(d.loe.end)}: no milestones to enter. Each period earns the hours planned for it once the period is reached. Change the dates in Deliverables.
        </p>
      )}
      <table className="w-full text-sm">
        <thead className="text-left text-xs text-slate-500">
          <tr>
            <th>#</th>
            <th>Milestone</th>
            <th className="text-right">Cum %</th>
            <th className="pr-3 text-right">Value</th>
            <th>Planned</th>
            <th>Forecast</th>
            <th>Earned</th>
          </tr>
        </thead>
        <tbody>
          {d.steps.map((s, i) => (
            <tr key={i} className="border-t border-slate-100">
              <td>{i + 1}</td>
              <td>{s.milestone}</td>
              <td className="num text-right">{chain ? formatCum(chain.steps[i]) : ""}</td>
              <td className="num pr-3 text-right">{chain ? fmtNum((st?.weight ?? d.weight) * chain.steps[i].cum, 1) : ""}</td>
              {(["planned", "forecast", "earned"] as const).map((f) => (
                <td key={f} className="py-1 pr-2">
                  <Input
                    type="date"
                    aria-label={`${s.milestone} ${f}`}
                    className={cn("h-8 w-36", f === "earned" && draft[i][f] && "border-emerald-400")}
                    value={draft[i][f]}
                    placeholder={f === "forecast" && !draft[i].forecast ? s.planned ?? "" : undefined}
                    disabled={lockedField(i, f)}
                    onChange={(e) => setDraft(draft.map((r, j) => (j === i ? { ...r, [f]: e.target.value } : r)))}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="mt-2 text-xs text-slate-500">An empty forecast means "same as planned". Earned dates in closed periods can't change.</p>
      {(issues.get(d._id) ?? []).length > 0 && (
        <ul className="mt-3 space-y-1 text-sm">
          {issues.get(d._id)!.map((x, k) => (
            <li key={k} className={x.severity === "error" ? "text-red-600" : "text-amber-700"}>
              {x.message}
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}
